import { useMemo } from 'react';
import { useGqlMutation, useGqlQuery } from '@API/useAvQuery';
import { QueryKey, uniqBy } from '@fabric-ui/utils';
import { useQueryClient } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { useSnackbar } from 'notistack';
import { useAvContext } from '../../context/AvContextProvider';
import { Permission, PermissionEntitiesNames } from '../../types';
import { FormattingCondition, FormattingRule } from './types';
import { NONE_ID } from './utils';

export const useFormattingRules = () => {
  const {
    userPermissions: { hasAllowedPermissionToResource },
  } = useAvContext();

  const hasPermissionsToReports = hasAllowedPermissionToResource({
    resource: PermissionEntitiesNames.REPORT,
    permission: Permission.READ,
  });
  return useGqlQuery<FormattingRule[]>({
    query: GET_FORMATTING_RULES,
    queryKey: [QueryKey.GETFORMATTINGRULES],
    enabled: hasPermissionsToReports,
  });
};

export interface EditCreateFormattingRule {
  formattingRule: FormattingRule;
  isCreate: boolean;
  onSuccess?: () => void;
}

export const cleanFormattingConditions = (formattingRule: Omit<FormattingRule, 'id'>) =>
  (formattingRule.formattingConditions as FormattingCondition[]).map(({ id, filter, ...rest }, index) => ({
    ...rest,
    ...(index === formattingRule.formattingConditions.length - 1 && formattingRule.fallbackLogic ? {} : { filter }),
  }));

export const useEditFormattingRule = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const onMutationSuccess = async (_, variables: { onSuccess?: () => void }) => {
    await queryClient.invalidateQueries({
      queryKey: [QueryKey.GETFORMATTINGRULES],
    });
    enqueueSnackbar('Formatting Rule saved successfully', { variant: 'success' });
    variables.onSuccess?.();
  };

  const onMutationError = (error: { message: string }) => {
    enqueueSnackbar(error.message, { variant: 'error', style: { whiteSpace: 'pre-line' } });
  };

  const variablesAdapter = ({ formattingRule }: { formattingRule: Omit<FormattingRule, 'id'> & { id?: string } }) => ({
    formattingRule: {
      ...formattingRule,
      formattingConditions: cleanFormattingConditions(formattingRule),
    },
  });

  const { mutate: create, isPending: isCreating } = useGqlMutation<
    any,
    { formattingRule: Omit<FormattingRule, 'id'>; onSuccess?: () => void }
  >({
    mutation: CREATE_FORMATTING_RULE,
    variablesAdapter,
    onSuccess: onMutationSuccess,
    onError: onMutationError,
  });

  const { mutate: update, isPending: isUpdating } = useGqlMutation<any, { formattingRule: FormattingRule; onSuccess?: () => void }>({
    mutation: UPDATE_FORMATTING_RULE,
    variablesAdapter,
    onSuccess: onMutationSuccess,
    onError: onMutationError,
  });

  const mutate = ({ formattingRule, isCreate, onSuccess }: EditCreateFormattingRule) => {
    const { id, ...rest } = formattingRule;
    if (isCreate) {
      create({ formattingRule: rest, onSuccess });
    } else {
      update({ formattingRule: { ...rest, id }, onSuccess });
    }
  };

  return { mutate, isPending: isCreating || isUpdating };
};

export const useDeleteFormattingRules = (onSuccess: () => void) => {
  const { enqueueSnackbar } = useSnackbar();

  return useGqlMutation<any, { ids: string[] }>({
    mutation: DELETE_FORMATTING_RULES,
    variablesAdapter: ({ ids }) => ({ ids }),
    onSuccess: () => {
      enqueueSnackbar(`Successfully Deleted Formatting Rules`, { variant: 'success' });
      onSuccess();
    },
  });
};

export const useFormattingRuleOptions = ({ additionalFormattingRule = [] }: { additionalFormattingRule?: FormattingRule[] }) => {
  const { data: formattingRules = [], isLoading: isLoadingFormattingRules } = useFormattingRules();
  const allFormattingRulesOptions = useMemo(
    () => uniqBy([{ name: 'None', id: NONE_ID }, ...additionalFormattingRule, ...formattingRules], v => v.id),
    [formattingRules, additionalFormattingRule]
  );

  return { formattingRules, isLoadingFormattingRules, allFormattingRulesOptions };
};

const GET_FORMATTING_RULES = gql`
  query findFormattingRules {
    findFormattingRules {
      id
      name
      fallbackLogic
      type
      fieldName
      formattingConditions {
        id
        color
        filter
      }
    }
  }
`;

const CREATE_FORMATTING_RULE = gql`
  mutation createFormattingRule($formattingRule: FormattingRuleDtoInput) {
    createFormattingRule(formattingRule: $formattingRule) {
      id
    }
  }
`;
const UPDATE_FORMATTING_RULE = gql`
  mutation updateFormattingRule($formattingRule: FormattingRuleDtoInput) {
    updateFormattingRule(formattingRule: $formattingRule) {
      id
    }
  }
`;

const DELETE_FORMATTING_RULES = gql`
  mutation deleteFormattingRules($ids: [String]) {
    deleteFormattingRules(ids: $ids)
  }
`;
